import { useState, useEffect, type ReactNode } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useAuthFetch } from '@/hooks';
import { PLATFORM_FEE_CENTS, type GenerateInvoicesRequest, type GenerateInvoicesResponse, type OperatorStatsResponse, type OperatorUserTrendResponse, type OperatorRevenueTrendResponse } from '@dermalink/shared';
import styles from './OperatorAnalytics.module.css';

function formatEUR(cents: number): string {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

type Period = '7d' | '30d' | '3m' | '6m' | 'all';
type Metric = 'signups' | 'messages' | 'conversations_started';
type Granularity = 'hour' | 'day' | 'month';

const PERIODS: [Period, string][] = [
  ['7d',  '7 dagen'],
  ['30d', '30 dagen'],
  ['3m',  '3 maanden'],
  ['6m',  '6 maanden'],
  ['all', 'Alles'],
];

const METRICS: [Metric, string][] = [
  ['signups',                'Nieuwe gebruikers'],
  ['messages',                'Berichten verzonden'],
  ['conversations_started',   'Gesprekken gestart'],
];

const GRANULARITY_LABELS: Record<Granularity, string> = {
  hour:  'Per uur',
  day:   'Per dag',
  month: 'Per maand',
};

// Which granularities can be picked per period — mirrors the API's own cap
// (see GET /operator/stats/user-trend), so the dropdown never offers a
// combination the backend would silently downgrade.
const GRANULARITY_OPTIONS: Record<Period, Granularity[]> = {
  '7d':  ['hour', 'day', 'month'],
  '30d': ['hour', 'day', 'month'],
  '3m':  ['day', 'month'],
  '6m':  ['day', 'month'],
  'all': ['month'],
};

export function OperatorAnalytics() {
  const authFetch = useAuthFetch();
  const [stats, setStats] = useState<OperatorStatsResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    authFetch('/operator/stats')
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data: OperatorStatsResponse) => setStats(data))
      .catch(() => setError(true));
  }, [authFetch]);

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <h1 className="page-heading">Statistieken</h1>

        {error && <p className="text-muted status-message">Statistieken konden niet worden geladen.</p>}

        {stats && (
          <>
            <StatSection title="Gebruikers">
              <StatCard label="Totaal" value={stats.total_users} />
              <StatCard label="Patiënten" value={stats.patients} />
              <StatCard label="Geverifieerde artsen" value={stats.verified_doctors} />
              <StatCard label="Artsen in verificatie" value={stats.pending_verification_doctors} highlight={stats.pending_verification_doctors > 0} />
              <StatCard label="Vergrendelde artsen" value={stats.locked_doctors} highlight={stats.locked_doctors > 0} />
              <StatCard label="Gedeactiveerd" value={stats.suspended_users} highlight={stats.suspended_users > 0} />
            </StatSection>

            <StatSection title="Conversaties">
              <StatCard label="Totaal" value={stats.total_conversations} />
              <StatCard label="Actief" value={stats.active_conversations} />
              <StatCard label="Gesloten" value={stats.closed_conversations} />
            </StatSection>

            <StatSection title="Financieel">
              <StatCard label="Verwerkte transacties" value={formatEUR(stats.total_revenue_cents)} />
              <StatCard label="Terugbetaald" value={formatEUR(stats.total_refunded_cents)} />
              <StatCard label="Netto verwerkt" value={formatEUR(stats.total_revenue_cents - stats.total_refunded_cents)} />
              <StatCard label="Openstaand" value={formatEUR(stats.outstanding_cents)} highlight={stats.outstanding_cents > 0} />
              <StatCard label="Betalingen" value={stats.total_payments} />
              <StatCard label="Terugbetalingen" value={stats.total_refunds} />
              <StatCard label="Platformvergoeding" value={formatEUR(stats.total_platform_fee_cents)} />
            </StatSection>
          </>
        )}

        <UserTrendChart />
        <RevenueTrendChart />
        <PlatformFeeTrendChart />
        <InvoiceGenerator />
      </div>
    </div>
  );
}

const NL_MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// Format is inferred from the bucket string itself ('YYYY-MM', 'YYYY-MM-DD'
// or 'YYYY-MM-DD HH24:00') rather than from the period, since granularity
// can now be overridden independently of period.
function bucketLabel(bucket: string): string {
  if (bucket.length === 7) {
    const m = parseInt(bucket.slice(5, 7), 10) - 1;
    return NL_MONTHS[m] ?? bucket;
  }
  if (bucket.includes(' ')) {
    const d = new Date(`${bucket.replace(' ', 'T')}:00`);
    return `${d.getDate()} ${NL_MONTHS[d.getMonth()]} ${String(d.getHours()).padStart(2, '0')}u`;
  }
  const d = new Date(`${bucket}T00:00:00`);
  return `${d.getDate()} ${NL_MONTHS[d.getMonth()]}`;
}

function TrendTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number; name: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      {payload.map((p) => (
        <div key={p.name}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
}

function UserTrendChart() {
  const authFetch = useAuthFetch();
  const [period, setPeriod] = useState<Period>('6m');
  const [metric, setMetric] = useState<Metric>('signups');
  const [granularity, setGranularity] = useState<Granularity | 'auto'>('auto');
  const [data, setData] = useState<OperatorUserTrendResponse | null>(null);
  const [error, setError] = useState(false);

  // A granularity chosen for one period may not be offered for another
  // (e.g. 'hour' is 30d-only) — fall back to auto rather than sending a
  // combination the picker no longer shows as selected.
  useEffect(() => {
    if (granularity !== 'auto' && !GRANULARITY_OPTIONS[period].includes(granularity)) {
      setGranularity('auto');
    }
  }, [period, granularity]);

  useEffect(() => {
    setData(null);
    setError(false);
    const params = new URLSearchParams({ period, metric });
    if (granularity !== 'auto') params.set('granularity', granularity);
    authFetch(`/operator/stats/user-trend?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OperatorUserTrendResponse) => setData(d))
      .catch(() => setError(true));
  }, [authFetch, period, metric, granularity]);

  const chartData = data?.points.map((p) => ({ ...p, label: bucketLabel(p.bucket) })) ?? [];
  const labelEvery = Math.max(1, Math.ceil(chartData.length / 8));

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div className={styles.pillGroup}>
          {METRICS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMetric(key)}
              className={`${styles.pill}${metric === key ? ` ${styles.pillActive}` : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={styles.pillRight}>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity | 'auto')}
            className={styles.select}
          >
            <option value="auto">Automatisch</option>
            {GRANULARITY_OPTIONS[period].map((g) => (
              <option key={g} value={g}>{GRANULARITY_LABELS[g]}</option>
            ))}
          </select>
          <div className={styles.pillGroup}>
            {PERIODS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPeriod(key)}
                className={`${styles.pill}${period === key ? ` ${styles.pillActive}` : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-muted status-message">Trend kon niet worden geladen.</p>}

      {!error && chartData.length === 0 && <p className="text-muted status-message">Laden…</p>}

      {chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} barCategoryGap="28%" margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#efe0d8" strokeDasharray="4 4" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              tickFormatter={(v: string, i: number) => (i % labelEvery === 0 ? v : '')}
              tick={{ fontSize: 11, fill: '#808080' }}
            />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={28} tick={{ fontSize: 10, fill: '#adadad' }} />
            <Tooltip content={<TrendTooltip />} cursor={{ fill: 'rgba(236,106,76,0.08)' }} />
            {metric === 'signups' ? (
              <>
                <Bar dataKey="patients" name="Patiënten" stackId="users" fill="#ec6a4c" radius={[0, 0, 0, 0]} />
                <Bar dataKey="doctors" name="Artsen" stackId="users" fill="#f8a488" radius={[3, 3, 0, 0]} />
              </>
            ) : (
              <Bar dataKey="count" name={METRICS.find(([key]) => key === metric)![1]} fill="#ec6a4c" radius={[3, 3, 0, 0]} />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function RevenueTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      <div>{formatEUR(payload[0].value)}</div>
    </div>
  );
}

function RevenueTrendChart() {
  const authFetch = useAuthFetch();
  const [period, setPeriod] = useState<Period>('6m');
  const [granularity, setGranularity] = useState<Granularity | 'auto'>('auto');
  const [data, setData] = useState<OperatorRevenueTrendResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (granularity !== 'auto' && !GRANULARITY_OPTIONS[period].includes(granularity)) {
      setGranularity('auto');
    }
  }, [period, granularity]);

  useEffect(() => {
    setData(null);
    setError(false);
    const params = new URLSearchParams({ period });
    if (granularity !== 'auto') params.set('granularity', granularity);
    authFetch(`/operator/stats/revenue-trend?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OperatorRevenueTrendResponse) => setData(d))
      .catch(() => setError(true));
  }, [authFetch, period, granularity]);

  const chartData = data?.points.map((p) => ({ ...p, label: bucketLabel(p.bucket) })) ?? [];
  const labelEvery = Math.max(1, Math.ceil(chartData.length / 8));

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div className={styles.statSectionTitle}>Verwerkte transacties</div>
        <div className={styles.pillRight}>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity | 'auto')}
            className={styles.select}
          >
            <option value="auto">Automatisch</option>
            {GRANULARITY_OPTIONS[period].map((g) => (
              <option key={g} value={g}>{GRANULARITY_LABELS[g]}</option>
            ))}
          </select>
          <div className={styles.pillGroup}>
            {PERIODS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPeriod(key)}
                className={`${styles.pill}${period === key ? ` ${styles.pillActive}` : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-muted status-message">Omzet kon niet worden geladen.</p>}

      {!error && chartData.length === 0 && <p className="text-muted status-message">Laden…</p>}

      {chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} barCategoryGap="28%" margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#efe0d8" strokeDasharray="4 4" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              tickFormatter={(v: string, i: number) => (i % labelEvery === 0 ? v : '')}
              tick={{ fontSize: 11, fill: '#808080' }}
            />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={40} tickFormatter={(v: number) => `€${Math.round(v / 100)}`} tick={{ fontSize: 10, fill: '#adadad' }} />
            <Tooltip content={<RevenueTooltip />} cursor={{ fill: 'rgba(236,106,76,0.08)' }} />
            <Bar dataKey="revenue_cents" name="Omzet" fill="#ec6a4c" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function PlatformFeeTrendChart() {
  const authFetch = useAuthFetch();
  const [period, setPeriod] = useState<Period>('6m');
  const [granularity, setGranularity] = useState<Granularity | 'auto'>('auto');
  const [data, setData] = useState<OperatorRevenueTrendResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (granularity !== 'auto' && !GRANULARITY_OPTIONS[period].includes(granularity)) {
      setGranularity('auto');
    }
  }, [period, granularity]);

  useEffect(() => {
    setData(null);
    setError(false);
    const params = new URLSearchParams({ period });
    if (granularity !== 'auto') params.set('granularity', granularity);
    authFetch(`/operator/stats/revenue-trend?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OperatorRevenueTrendResponse) => setData(d))
      .catch(() => setError(true));
  }, [authFetch, period, granularity]);

  const chartData = data?.points.map((p) => ({
    label:     bucketLabel(p.bucket),
    fee_cents: p.platform_fee_cents,
  })) ?? [];
  const labelEvery = Math.max(1, Math.ceil(chartData.length / 8));

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div className={styles.statSectionTitle}>Platformvergoeding</div>
        <div className={styles.pillRight}>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity | 'auto')}
            className={styles.select}
          >
            <option value="auto">Automatisch</option>
            {GRANULARITY_OPTIONS[period].map((g) => (
              <option key={g} value={g}>{GRANULARITY_LABELS[g]}</option>
            ))}
          </select>
          <div className={styles.pillGroup}>
            {PERIODS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPeriod(key)}
                className={`${styles.pill}${period === key ? ` ${styles.pillActive}` : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-muted status-message">Vergoeding kon niet worden geladen.</p>}

      {!error && chartData.length === 0 && <p className="text-muted status-message">Laden…</p>}

      {chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} barCategoryGap="28%" margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#efe0d8" strokeDasharray="4 4" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              tickFormatter={(v: string, i: number) => (i % labelEvery === 0 ? v : '')}
              tick={{ fontSize: 11, fill: '#808080' }}
            />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={40} tickFormatter={(v: number) => `€${Math.round(v / 100)}`} tick={{ fontSize: 10, fill: '#adadad' }} />
            <Tooltip content={<RevenueTooltip />} cursor={{ fill: 'rgba(236,106,76,0.08)' }} />
            <Bar dataKey="fee_cents" name="Platformvergoeding" fill="#f0955f" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}

      <p className={styles.chartNote}>
        Ingehouden op betalingen naar het eigen Mollie-account van praktijken: {formatEUR(PLATFORM_FEE_CENTS.new_consult)} per nieuw consult, {formatEUR(PLATFORM_FEE_CENTS.prescription)} per voorschriftenconsult, incl. 21% btw.
      </p>
    </div>
  );
}

/** Issues last month's platform-fee invoices (one per practice on its own Mollie account). */
function InvoiceGenerator() {
  const authFetch = useAuthFetch();
  const lastMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
  const [month, setMonth]   = useState(`${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`);
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function handleGenerate() {
    setBusy(true);
    setResult(null);
    const [year, m] = month.split('-').map(Number);
    try {
      const res = await authFetch('/operator/invoices/generate', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ year, month: m } satisfies GenerateInvoicesRequest),
      });
      const body = await res.json() as GenerateInvoicesResponse & { message?: string };
      if (!res.ok) throw new Error(body.message ?? 'Facturen aanmaken mislukt.');
      setResult(body.invoices.length
        ? `${body.invoices.length} ${body.invoices.length === 1 ? 'factuur' : 'facturen'} aangemaakt en gemaild: ${body.invoices.map((i) => `${i.invoice_number} (${i.organization_name}, ${formatEUR(i.total_cents)})`).join(', ')}.`
        : 'Geen nieuwe platformvergoedingen om te factureren voor deze maand.');
    } catch (err) {
      setResult((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div className={styles.statSectionTitle}>Facturen platformvergoeding</div>
        <div className={styles.pillRight}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={styles.select} aria-label="Maand" />
          <button type="button" className={`${styles.pill} ${styles.pillActive}`} onClick={() => { void handleGenerate(); }} disabled={busy}>
            {busy ? 'Bezig…' : 'Facturen aanmaken'}
          </button>
        </div>
      </div>
      <p className={styles.chartNote}>
        Maakt per praktijk één factuur voor de nog niet gefactureerde platformvergoedingen tot en met de gekozen maand, en mailt die naar de praktijkbeheerders. Opnieuw uitvoeren voor dezelfde maand maakt geen dubbele facturen.
      </p>
      {result && <p className={styles.chartNote}>{result}</p>}
    </div>
  );
}

function StatSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.statSection}>
      <div className={styles.statSectionTitle}>{title}</div>
      <div className={styles.statGrid}>
        {children}
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight = false }: { label: string; value: number | string; highlight?: boolean }) {
  return (
    <div className={`${styles.statCard}${highlight ? ` ${styles.statCardHighlight}` : ''}`}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}
